import React, { useEffect, useMemo, useRef, useState } from "react";
import { storage, authReady, auth } from "./firebase";
import { ref, uploadBytesResumable } from "firebase/storage";

function App() {
  // ========== Inline Dark Theme CSS ==========
  const css = `
    :root {
      --bg: #0b1220;           /* deep blue-gray */
      --card: #121a2b;        /* darker card */
      --text: #e6eefc;        /* light text */
      --muted: #9db2d7;       /* muted text */
      --border: #20304c;      /* soft border */
      --primary: #4f7cff;     /* blue accent */
      --primary-600: #3a60d9; /* darker blue */
      --neutral-btn: #16223a; /* button bg */
      --neutral-btn-border: #2a3d64;
      --radius: 14px;
      --shadow: 0 12px 32px rgba(3, 10, 24, 0.5);
      --success: #3ddc97;
      --warning: #f5c542;
    }
    * { box-sizing: border-box; }
    html, body, #root { height: 100%; }
    body {
      margin: 0;
      background: radial-gradient(1200px 600px at 10% -10%, #0f1a33 0%, var(--bg) 45%) fixed;
      color: var(--text);
      font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    a { color: var(--primary); text-decoration: none; }
    .container { max-width: 980px; margin: 0 auto; padding: 16px; }

    .header {
      display: flex; align-items: center; justify-content: space-between;
      gap: 12px; margin-bottom: 12px;
    }
    .title { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: 0.2px; }
    .pill { color: var(--muted); font-size: 13px; }

    .card {
      background: linear-gradient(160deg, rgba(18,26,43,0.96), rgba(18,26,43,0.86));
      border: 1px solid var(--border);
      border-radius: var(--radius);
      box-shadow: var(--shadow);
      padding: 16px;
      backdrop-filter: blur(4px);
    }

    .row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 6px; }

    .progress {
      width: 100%;
      height: 10px;
      border-radius: 999px;
      background: #0e1628;
      border: 1px solid var(--border);
      overflow: hidden;
      margin-top: 6px;
    }
    .progress > span {
      display: block;
      height: 100%;
      background: linear-gradient(90deg, var(--primary), var(--primary-600));
      width: 0%;
      box-shadow: inset 0 0 10px rgba(79,124,255,0.6);
      transition: width 220ms ease;
    }

    .video {
      margin-top: 12px;
      width: 100%;
      border-radius: 12px;
      border: 1px solid var(--border);
      background: #000;
      overflow: hidden;
    }

    .controls, .label-buttons {
      display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px;
    }

    .btn {
      --pad-y: 11px; --pad-x: 16px;
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      padding: var(--pad-y) var(--pad-x);
      border-radius: 12px;
      border: 1px solid transparent;
      font-size: 14px; font-weight: 700; letter-spacing: 0.2px;
      cursor: pointer;
      transition: transform 0.06s ease, box-shadow 0.15s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
      box-shadow: 0 2px 0 rgba(0,0,0,0.2);
      user-select: none; -webkit-tap-highlight-color: transparent;
    }
    .btn:active { transform: translateY(1px); }

    .btn-primary {
      color: #0a1222;
      background: linear-gradient(180deg, #6a96ff, var(--primary));
      border-color: #2a4bb0;
      text-shadow: 0 1px 0 rgba(255,255,255,0.2);
    }
    .btn-primary:hover { filter: brightness(1.05); }

    .btn-outline {
      background: var(--neutral-btn);
      color: var(--text);
      border-color: var(--neutral-btn-border);
    }
    .btn-outline:hover {
      border-color: var(--primary);
      color: #dfe9ff;
      box-shadow: 0 0 0 3px rgba(79,124,255,0.2) inset;
    }

    .btn-ghost {
      background: transparent;
      color: var(--muted);
      border: 1px solid var(--border);
    }
    .btn-ghost:hover { color: #cfe2ff; border-color: var(--primary); }

    .switch {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 8px 10px; border-radius: 10px;
      background: var(--neutral-btn);
      border: 1px solid var(--neutral-btn-border);
      color: var(--muted); font-weight: 700;
    }

    .login { max-width: 520px; margin: 10vh auto; padding: 18px; }
    .field { margin-top: 12px; }
    .label { font-weight: 700; color: var(--muted); font-size: 13px; }
    .input, .select {
      width: 100%; margin-top: 6px; padding: 12px 14px; border-radius: 12px;
      border: 1px solid var(--border); background: #0f1830; color: var(--text);
      font-size: 15px; outline: none;
      transition: box-shadow 0.15s ease, border-color 0.15s ease;
    }
    .input::placeholder { color: #6c7da6; }
    .input:focus, .select:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 4px rgba(79,124,255,0.2);
    }
    .check { display: inline-flex; align-items: center; gap: 8px; color: var(--muted); font-weight: 700; margin-top: 8px; }

    .end-card {
      text-align: center;
      padding: 40px 20px;
      background: linear-gradient(160deg, rgba(18,26,43,0.96), rgba(18,26,43,0.86));
      border: 1px solid var(--border);
      border-radius: 16px;
      box-shadow: var(--shadow);
      margin-top: 20vh;
    }
    .end-title {
      font-size: 28px;
      font-weight: 900;
      margin: 0 0 6px 0;
      letter-spacing: 0.3px;
      color: var(--success);
      text-shadow: 0 2px 12px rgba(61,220,151,0.25);
    }
    .end-sub {
      color: var(--muted);
      font-size: 15px;
      margin: 0;
    }

    @media (max-width: 640px) {
      .header { flex-direction: column; align-items: flex-start; gap: 6px; }
      .controls { gap: 10px; }
      .btn { width: 100%; }
      .label-buttons { gap: 10px; }
      .pill { font-size: 12px; }
      .end-card { margin-top: 14vh; padding: 28px 16px; }
      .end-title { font-size: 24px; }
    }
  `;

  // ========== Config ==========
  const DEFAULT_LABELS = ["Happy", "Sad", "Neutral", "Angry", "Surprised"];
  const CULTURE_OPTIONS = useMemo(() => {
  const base = [
    "Zimbabwean", "Zambian", "Yemeni", "Western European", "West African", "Welsh",
    "Vietnamese", "Venezuelan", "Uzbek", "Uruguayan", "Ukrainian", "Ugandan",
    "Turkmen", "Turkish", "Tunisian", "Trinidadian", "Tongan", "Togolese", "Tibetan",
    "Thai", "Tanzanian", "Tajik", "Taiwanese", "Syrian", "Swiss", "Swedish", "Swazi",
    "Sudanese", "Sri Lankan", "Spanish", "Southeast Asian", "South Sudanese",
    "South Korean", "South Asian", "South American", "South African", "Somali",
    "Slovenian", "Slovak", "Slavic", "Singaporean", "Sierra Leonean", "Seychellois",
    "Serbian", "Senegalese", "Scandinavian", "Scottish", "Saudi", "Samoan", "Sami",
    "Salvadoran", "Rwandan", "Russian", "Romanian", "Quebecois", "Qatari",
    "Puerto Rican", "Portuguese", "Polynesian", "Polish", "Peruvian", "Persian",
    "Paraguayan", "Papua New Guinean", "Panamanian", "Pakistani",
    "Pacific Islander", "Omani", "Oceanian", "Norwegian", "Northern Irish",
    "North Macedonian", "North American", "North African", "Nigerien", "Nigerian",
    "Nicaraguan", "New Zealander", "Nepalese", "Native American", "Namibian",
    "Mozambican", "Moroccan", "Montenegrin", "Mongolian", "Monacan", "Moldovan",
    "Micronesian", "Mexican", "Melanesian", "Mauritian", "Mauritanian", "Maori",
    "Maltese", "Malian", "Malaysian", "Malawian", "Malagasy", "Macedonian",
    "Luxembourgish", "Lithuanian", "Libyan", "Liberian", "Lebanese", "Latvian",
    "Latino", "Laotian", "Kyrgyz", "Kuwaiti", "Kurdish", "Kosovar", "Korean",
    "Kenyan", "Kazakh", "Jordanian", "Japanese", "Jamaican", "Ivorian", "Italian",
    "Israeli", "Irish", "Iraqi", "Iranian", "Indonesian", "Indigenous", "Indian",
    "Icelandic", "Hungarian", "Honduran", "Hispanic", "Hawaiian", "Haitian",
    "Guyanese", "Guinean", "Guatemalan", "Grenadian", "Greenlandic", "Greek",
    "Ghanaian", "German", "Georgian", "Gambian", "Gabonese", "French", "Finnish",
    "Filipino", "Fijian", "European", "Ethiopian", "Estonian", "Eritrean",
    "Equatorial Guinean", "English", "Emirati", "Egyptian", "Ecuadorian", "Dutch",
    "Dominican", "Djiboutian", "Danish", "Czech", "Cypriot", "Cuban", "Croatian",
    "Costa Rican", "Congolese", "Colombian", "Chinese", "Chilean", "Chadian",
    "Central African", "Catalan", "Caribbean", "Cape Verdean", "Canadian",
    "Cameroonian", "Cambodian", "Burundian", "Burmese", "Burkinabe", "Bulgarian",
    "Bruneian", "British", "Brazilian", "Botswanan", "Bosnian", "Bolivian",
    "Bhutanese", "Berber", "Beninese", "Belizean", "Belgian", "Belarusian",
    "Basque", "Barbadian", "Bangladeshi", "Bahamian", "Azerbaijani", "Austrian",
    "Australian", "Asian", "Armenian", "Argentinian", "Arab", "Angolan", "Andorran",
    "American", "Algerian", "Albanian", "African", "Afghan"
  ];

    base.sort()
    return base.concat("Other");
  }, []);

  // ========== Auth gate (no persistence) ==========
  const [authed, setAuthed] = useState(false);
  const [loginName, setLoginName] = useState("");
  const [loginAnon, setLoginAnon] = useState(false);
  const [loginCulture, setLoginCulture] = useState("");
  const [loginCultureOther, setLoginCultureOther] = useState("");

  // ========== App state ==========
  const [labels] = useState(DEFAULT_LABELS);
  const [videos, setVideos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [rows, setRows] = useState([]);
  const [raterName, setRaterName] = useState("");
  const [raterCulture, setRaterCulture] = useState("");
  const [autoplay, setAutoplay] = useState(true);
  const [uploadPct, setUploadPct] = useState(0);
  const [finished, setFinished] = useState(false);
  const [uploading, setUploading] = useState(false);

  const videoRef = useRef(null);

  // Load videos after login and reset everything session-wise
  useEffect(() => {
    if (!authed) return;
    fetch("/vid_list.json", { cache: "no-store" })
      .then((r) => r.json())
      .then((obj) => {
        if (obj && typeof obj === "object" && !Array.isArray(obj)) {
          const arr = Object.entries(obj).map(([k, v]) => {
            const url = "/" + String(v).replace(/\\/g, "/").replace(/^\/+/, "");
            const name = url.split("/").pop().replace(/\.mp4$/i, "");
            return { name, url };
          });
          setVideos(arr);
          setIdx(0);
          setRows([]);
          setFinished(false);
          setUploadPct(0);
        } else {
          console.error("vid_list.json must be an object like {file0: 'video\\\\culture_emotion_index.mp4'}");
        }
      })
      .catch((e) => console.error("Failed to load vid_list.json", e));
  }, [authed]);

  // Helpers
  const resolveLoginCulture = () =>
    loginCulture === "Other" ? loginCultureOther.trim() : loginCulture;

  const parseFileInfo = (url) => {
    const file = (url || "").split("/").pop() || "";
    const stem = file.replace(/\.[^.]+$/, "");
    const [file_culture = "", file_emotion = "", file_index = ""] = stem.split("_");
    return { file_culture, file_emotion, file_index };
  };

  const progressPct = useMemo(
    () => (videos.length ? Math.round(((idx + 1) / videos.length) * 100) : 0),
    [idx, videos.length]
  );

  function handleLogin(e) {
    e.preventDefault();
    const name = (loginName || "").trim();
    const finalName = loginAnon || !name ? "Anonymous" : name;
    const finalCulture = resolveLoginCulture();
    if (!finalCulture) {
      alert("Please select your culture (or specify it if 'Other').");
      return;
    }
    setRaterName(finalName);
    setRaterCulture(finalCulture);
    setAuthed(true); // resets on refresh by design
  }

  function handleLogout() {
    setAuthed(false);
    setLoginName("");
    setLoginAnon(false);
    setLoginCulture("");
    setLoginCultureOther("");
    setVideos([]);
    setIdx(0);
    setRows([]);
    setUploadPct(0);
    setFinished(false);
  }

  // Hotkeys
  useEffect(() => {
    if (!authed || finished) return;
    const onKey = (e) => {
      if (e.target && e.target.tagName === "INPUT" && e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      const num = Number(e.key);
      if (Number.isInteger(num) && num >= 1 && num <= labels.length) {
        chooseLabel(labels[num - 1]);
      } else if (e.key === "ArrowRight") {
        setIdx((i) => Math.min(i + 1, videos.length - 1));
      } else if (e.key === "ArrowLeft") {
        setIdx((i) => Math.max(i - 1, 0));
      } else if (e.key.toLowerCase() === "u") {
        undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [labels, videos.length, authed, idx, rows, finished]);

  // CSV helpers (with user_name column)
  const buildCsvText = (data) => {
    const headers = [
      "video_name","file_culture","file_emotion","file_index",
      "selected_label","timestamp_ms","video_time_s",
      "user_name","rater","rater_culture"
    ];
  const escapeCell = (v) => {
    const s = (v ?? "").toString();
    if (/[",\n]/.test(s)) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
    const lines = [headers.join(",")].concat(
      data.map((r) =>
        [
          r.video_name, r.file_culture, r.file_emotion, r.file_index,
          r.selected_label, r.timestamp_ms, (r.video_time_s ?? 0).toFixed(3),
          r.user_name, r.rater, r.rater_culture
        ].map(escapeCell).join(",")
      )
    );
    return "\uFEFF" + lines.join("\n");
  };

  // Label selection; on last -> auto-upload (no result shown)
  function chooseLabel(label) {
    if (!videos[idx] || uploading || finished) return;
    const now = Date.now();
    const t = videoRef.current ? videoRef.current.currentTime : 0;
    const parsed = parseFileInfo(videos[idx].url);
    const row = {
      video_name: videos[idx].name,
      ...parsed,
      selected_label: label,
      timestamp_ms: now,
      video_time_s: t,
      user_name: raterName || "Anonymous",
      rater: raterName || "Anonymous",
      rater_culture: raterCulture || "",
    };
    setRows((prev) => {
      const nextRows = [...prev, row];
      const isLast = idx >= videos.length - 1;
      if (isLast) {
        setTimeout(() => autoUpload(nextRows), 0);
      } else {
        setIdx((i) => Math.min(i + 1, videos.length - 1));
      }
      return nextRows;
    });
  }

  function undo() {
    if (uploading || finished) return;
    setRows((r) => r.slice(0, -1));
    setIdx((i) => Math.max(i - 1, 0));
  }

  async function autoUpload(finalRows) {
    if (!finalRows?.length || uploading || finished) {
      setFinished(true);
      return;
    }
    setUploading(true);
    await authReady; // ensure request.auth != null per your rules
    try {
      const csvText = buildCsvText(finalRows);
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });

      // Upload directly to /results/
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const path = `results/labels_${stamp}.csv`;

      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, blob, {
        contentType: "text/csv",
        cacheControl: "no-store",
        customMetadata: {
          user_name: raterName || "Anonymous",
          rater_culture: raterCulture || "",
          rows: String(finalRows.length),
        },
      });

      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setUploadPct(pct);
        },
        (err) => {
          console.error("Auto-upload failed:", err);
          // no result link shown; just mark finished
          setUploading(false);
          setFinished(true);
        },
        async () => {
          // success (no result link shown)
          setUploading(false);
          setFinished(true);
        }
      );
    } catch (e) {
      console.error(e);
      setUploading(false);
      setFinished(true);
    }
  }

  // ======== Login UI ========
  if (!authed) {
    const showOther = loginCulture === "Other";
    return (
      <>
        <style>{css}</style>
        <div className="login card">
          <h2 className="title">Sign in</h2>
          <p className="pill">Enter your name (or leave blank for Anonymous), and choose your culture.</p>
          <form onSubmit={handleLogin}>
            <div className="field">
              <div className="label">Name (leave empty for Anonymous)</div>
              <input
                className="input"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                placeholder="Your name"
                disabled={loginAnon}
              />
            </div>

            <label className="check">
              <input
                type="checkbox"
                checked={loginAnon}
                onChange={(e) => setLoginAnon(e.target.checked)}
              />
              Continue as Anonymous
            </label>

            <div className="field">
              <div className="label">Culture (required)</div>
              <select
                className="select"
                value={loginCulture}
                onChange={(e) => setLoginCulture(e.target.value)}
                required
              >
                <option value="" disabled>Select culture</option>
                {CULTURE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {showOther && (
              <div className="field">
                <div className="label">Specify culture</div>
                <input
                  className="input"
                  value={loginCultureOther}
                  onChange={(e) => setLoginCultureOther(e.target.value)}
                  placeholder="Type your culture"
                  required
                />
              </div>
            )}

            <div className="controls" style={{ marginTop: 16 }}>
              <button type="submit" className="btn btn-primary">Continue</button>
            </div>
          </form>
        </div>
      </>
    );
  }

  // ======== End screen (after upload finishes) ========
  if (finished && !uploading) {
    return (
      <>
        <style>{css}</style>
        <div className="container">
          <div className="end-card">
            <h1 className="end-title">Thank you! 🎉</h1>
            <p className="end-sub">
              Your labels have been submitted successfully. You may close this window.
            </p>
            <div className="controls" style={{ justifyContent: "center", marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => window.location.reload()}>
                Start Over
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ======== Main App ========
  const current = videos[idx];

  return (
    <>
      <style>{css}</style>
      <div className="container">
        <div className="header">
          <h2 className="title">Video Labeler</h2>
          <div className="pill">
            Hello, <strong>{raterName || "Anonymous"}</strong> — {raterCulture || "Unknown culture"}{" "}
            <button className="btn btn-ghost" onClick={handleLogout} style={{ marginLeft: 8 }}>
              Logout
            </button>
          </div>
        </div>
<div className="card">
  <div className="row">
    <div className="pill">
      Video {Math.min(idx + 1, videos.length)} / {videos.length}
    </div>
    <div className="pill">{progressPct}%</div>
  </div>
  <div className="progress" aria-label="progress">
    <span style={{ width: `${progressPct}%` }} />
  </div>


  <div
    style={{
      background: "rgba(79,124,255,0.1)",
      border: "1px solid rgba(79,124,255,0.3)",
      borderRadius: "10px",
      padding: "10px 14px",
      marginTop: "16px",
      color: "#dbe4ff",
      fontSize: "15px",
      lineHeight: "1.5",
    }}
  >
    <strong>Instructions:</strong> Please watch each video carefully and select the label
    that best describes the emotion displayed by the person in the video. Try to
    rate each clip as accurately as possible based on your perception.
  </div>


  {current ? (
    <video
      ref={videoRef}
      key={current.url}
      src={current.url}
      controls
      autoPlay={autoplay}
      className="video"
    />
  ) : (
    <p className="pill" style={{ marginTop: 12 }}>No video loaded.</p>
  )}

  <div className="label-buttons">
    {labels.map((lab, i) => (
      <button
        key={i}
        onClick={() => chooseLabel(lab)}
        className="btn btn-outline"
        disabled={uploading}
      >
        {i + 1}. {lab}
      </button>
    ))}
  </div>


          <div className="controls">
            <span className="switch">
              <input
                type="checkbox"
                checked={autoplay}
                onChange={(e) => setAutoplay(e.target.checked)}
                id="autoplay"
                disabled={uploading}
              />
              <label htmlFor="autoplay">Autoplay</label>
            </span>

            <button
              className="btn btn-outline"
              onClick={() => setIdx((i) => Math.max(i - 1, 0))}
              disabled={idx === 0 || uploading}
            >
              ⟵ Prev
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setIdx((i) => Math.min(i + 1, videos.length - 1))}
              disabled={idx >= videos.length - 1 || uploading}
            >
              Next ⟶
            </button>
          </div>

          {uploading && (
            <p className="pill" style={{ marginTop: 10 }}>
              Uploading… {uploadPct}% 
            </p>
          )}
        </div>
      </div>
    </>
  );
}

export default App;
